import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
} from 'react'

export type PriceMode = 'monthly' | 'yearly'

const PRICE_MODE_STORAGE_KEY = 'popety_rental_price_mode'
const DEFAULT_PRICE_MODE: PriceMode = 'yearly'

const getPriceModeFromStorage = (): PriceMode => {
  try {
    const stored = localStorage.getItem(PRICE_MODE_STORAGE_KEY)
    if (stored === 'monthly' || stored === 'yearly') {
      return stored
    }
    return DEFAULT_PRICE_MODE
  } catch {
    return DEFAULT_PRICE_MODE
  }
}

const savePriceModeToStorage = (mode: PriceMode): void => {
  try {
    localStorage.setItem(PRICE_MODE_STORAGE_KEY, mode)
  } catch (error) {
    console.error('Error saving price mode to localStorage:', error)
  }
}

type PropertyType =
  | 'house_apartment'
  | 'house'
  | 'apartment'
  | 'commercial'
  | 'plot'

export interface RentalMarketService {
  getRentalPriceEvolutionData: (filters: any) => Promise<any>
  getRentalPriceM2EvolutionData: (filters: any) => Promise<any>
  getRentalAvailabilityData: (filters: any) => Promise<any>
  getRentalPricePerRoomData: (filters: any) => Promise<any>
  getRentalPriceM2PerRoomData: (filters: any) => Promise<any>
  getRentalDistributionData: (filters: any) => Promise<any>
}

interface RentalMarketContextType {
  filters: {
    locationType: 'postal_code' | 'city' | 'district'
    propertyType: PropertyType
    location: string | null
    propertyCategory: string[]
  }
  updateLocationType: (type: 'postal_code' | 'city' | 'district') => void
  updatePropertyType: (type: PropertyType) => void
  getLocationDisplay: () => string
  service: RentalMarketService
  priceMode: PriceMode
  updatePriceMode: (mode: PriceMode) => void
}

const RentalMarketContext = createContext<RentalMarketContextType | undefined>(
  undefined
)

interface RentalMarketProviderProps {
  children: ReactNode
  landData: any
  service: RentalMarketService
  initialPropertyType?: PropertyType
  initialLocationType?: 'postal_code' | 'city' | 'district'
  onFilterChange?: (filters: {
    locationType: 'postal_code' | 'city' | 'district'
    propertyType: PropertyType
    location: string | null
    propertyCategory: string[]
  }) => void
}

export const RentalMarketProvider: React.FC<RentalMarketProviderProps> = ({
  children,
  landData,
  service,
  initialPropertyType,
  initialLocationType,
  onFilterChange,
}) => {
  const [locationType, setLocationType] = useState<
    'postal_code' | 'city' | 'district'
  >(initialLocationType || 'postal_code')
  const isInitialRender = useRef(true)
  const [propertyType, setPropertyType] = useState<PropertyType>(
    initialPropertyType || 'house_apartment'
  )
  const [priceMode, setPriceMode] = useState<PriceMode>(() =>
    getPriceModeFromStorage()
  )

  const updatePriceMode = useCallback((mode: PriceMode) => {
    setPriceMode(mode)
    savePriceModeToStorage(mode)
  }, [])

  const getLocation = (): string | null => {
    if (!landData) return null

    switch (locationType) {
      case 'postal_code':
        return landData.address_postal_code_name || null
      case 'city':
        return landData.municipality_name || null
      case 'district':
        return landData.district_name || null
      default:
        return null
    }
  }

  const getLocationDisplay = (): string => {
    if (!landData) return 'N/A'

    // Return the selected location level
    const location = getLocation()
    return location || 'N/A'
  }

  const getPropertyCategory = (): string[] => {
    switch (propertyType) {
      case 'house_apartment':
        return ['House', 'Apartment']
      case 'apartment':
        return ['Apartment']
      case 'house':
        return ['House']
      case 'commercial':
        return ['Commercial']
      case 'plot':
        return ['Plot']
      default:
        return []
    }
  }

  const filters = {
    locationType,
    propertyType,
    location: getLocation(),
    propertyCategory: getPropertyCategory(),
  }

  const updateLocationType = (type: 'postal_code' | 'city' | 'district') => {
    setLocationType(type)
  }

  const updatePropertyType = (type: PropertyType) => {
    setPropertyType(type)
  }

  // Notify parent when filters change (skip initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    onFilterChange?.(filters)
  }, [locationType, propertyType])

  return (
    <RentalMarketContext.Provider
      value={{
        filters,
        updateLocationType,
        updatePropertyType,
        getLocationDisplay,
        service,
        priceMode,
        updatePriceMode,
      }}
    >
      {children}
    </RentalMarketContext.Provider>
  )
}

export const useRentalMarket = () => {
  const context = useContext(RentalMarketContext)
  if (!context) {
    throw new Error(
      'useRentalMarket must be used within a RentalMarketProvider'
    )
  }
  return context
}
